import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import PropTypes from "prop-types";

import { Autocomplete, CircularProgress, debounce } from "@mui/material";
import { CustomInputField } from "../custom-input-field";

const DEFAULT_PAGE_SIZE = 15;

export const CustomAsyncAutocomplete = ({
  fetchOptions,
  TextFieldProps = {},
  value,
  onChange,
  disabled,
  getOptionLabel,
  isOptionEqualToValue,
  refetchKey,
  pageSize = DEFAULT_PAGE_SIZE,
  ...props
}) => {
  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [options, setOptions] = useState([]);

  // Kept in refs so the scroll handler and debounce always see the latest
  // values without being re-created (which would reset the debounce).
  const fetchOptionsRef = useRef(fetchOptions);
  const isEqualRef = useRef(isOptionEqualToValue);
  useEffect(() => {
    fetchOptionsRef.current = fetchOptions;
    isEqualRef.current = isOptionEqualToValue;
  }, [fetchOptions, isOptionEqualToValue]);

  // Pagination bookkeeping.
  const searchRef = useRef(undefined);
  const pageRef = useRef(0);
  const hasMoreRef = useRef(false);
  const loadingRef = useRef(false);

  const load = useCallback(
    async (searchValue, page) => {
      loadingRef.current = true;
      setIsLoading(true);
      if (page === 0) setOptions([]);
      try {
        const data =
          (await fetchOptionsRef.current(searchValue, {
            page,
            size: pageSize,
          })) ?? [];

        searchRef.current = searchValue;
        pageRef.current = page;
        hasMoreRef.current = data.length >= pageSize;

        setOptions((prev) => {
          if (page === 0) return data;
          // Safety net: only append genuinely new options, and stop paginating
          // if a page brought nothing new (e.g. a consumer that ignores `page`).
          const fresh = data.filter(
            (option) => !prev.some((p) => isEqualRef.current(option, p)),
          );
          if (fresh.length === 0) hasMoreRef.current = false;
          return [...prev, ...fresh];
        });
      } finally {
        loadingRef.current = false;
        setIsLoading(false);
      }
    },
    [pageSize],
  );

  const debouncedSearch = useMemo(
    // eslint-disable-next-line react-hooks/refs
    () => debounce((searchValue) => load(searchValue, 0), 500),
    [load],
  );

  // Explicit invalidation: drop cached options so the next open refetches.
  useEffect(() => {
    setOptions([]);
    pageRef.current = 0;
    hasMoreRef.current = false;
  }, [refetchKey]);

  const handleOpen = () => {
    setOpen(true);
    load(undefined, 0);
  };

  const handleClose = () => {
    setOpen(false);
    setOptions([]);
    pageRef.current = 0;
    hasMoreRef.current = false;
  };

  const handleListboxScroll = (event) => {
    if (loadingRef.current || !hasMoreRef.current) return;
    const node = event.currentTarget;
    if (node.scrollTop + node.clientHeight >= node.scrollHeight - 8) {
      load(searchRef.current, pageRef.current + 1);
    }
  };

  return (
    <Autocomplete
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      value={value || null}
      onInputChange={(_, value, reason) => {
        if (reason === "input") debouncedSearch(value);
      }}
      onChange={(_, newValue) => onChange(newValue)}
      options={options}
      isOptionEqualToValue={isOptionEqualToValue}
      getOptionLabel={getOptionLabel}
      loading={isLoading}
      filterOptions={(options) => options}
      slotProps={{ listbox: { onScroll: handleListboxScroll } }}
      renderInput={(params) => (
        <CustomInputField
          {...TextFieldProps}
          id={params.id}
          fullWidth={params.fullWidth}
          disabled={params.disabled}
          InputProps={{
            ...params.slotProps.input,
            ...TextFieldProps?.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress color="inherit" size={20} />
                ) : null}
                {params.slotProps.input.endAdornment}
              </>
            ),
          }}
          inputProps={{
            ...params.slotProps.htmlInput,
            ...TextFieldProps?.inputProps,
          }}
        />
      )}
      disabled={disabled}
      {...props}
    />
  );
};

CustomAsyncAutocomplete.propTypes = {
  // fetchOptions(searchValue, { page, size }) => option[] — return a page's worth
  // of options; the component appends pages as the user scrolls.
  fetchOptions: PropTypes.func.isRequired,
  TextFieldProps: PropTypes.object,
  value: PropTypes.any,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  getOptionLabel: PropTypes.func.isRequired,
  isOptionEqualToValue: PropTypes.func.isRequired,
  // Optional explicit invalidation: change this primitive to drop the cached
  // options (load-on-open already keeps the list fresh for most cases).
  refetchKey: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Rows fetched per page for the infinite-scroll list.
  pageSize: PropTypes.number,
};
